import { memo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { Shirt, GraduationCap } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { RentalType } from "@/shared/types/enums";
import { SellerSelectField } from "../../SellerSelectField";
import type { RentalCreateFormSchema } from "../../rental-create-schema";
import { StepHeading } from "./StepHeading";

interface StepTypeProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  onTypeChange: (type: RentalType.SUIT | RentalType.GOWN) => void;
  onSellerChange: (sellerId: number | null, sellerName: string | null) => void;
}

const TYPE_OPTIONS = [
  {
    value: RentalType.SUIT,
    label: "Aluguel de Terno",
    description: "Peças avulsas adicionadas por código de barras",
    icon: Shirt,
  },
  {
    value: RentalType.GOWN,
    label: "Aluguel de Beca",
    description: "Itens de beca por produto e quantidade",
    icon: GraduationCap,
  },
] as const;

/**
 * Etapa 1 — tipo de aluguel (cards selecionáveis) e vendedor.
 *
 * O tipo precisa ser o primeiro passo porque define se a etapa de itens
 * usa peças (SUIT) ou itens de beca (GOWN). Trocar o tipo limpa o que
 * não se aplica (tratado pela page via `onTypeChange`).
 */
export const StepType = memo(function StepType({
  form,
  onTypeChange,
  onSellerChange,
}: StepTypeProps) {
  const rentalType = useWatch({ control: form.control, name: "rentalType" });
  const sellerId = useWatch({ control: form.control, name: "sellerId" });

  return (
    <div className="space-y-6">
      <StepHeading
        title="Tipo de aluguel"
        description="Escolha o tipo e o vendedor responsável."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TYPE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = rentalType === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onTypeChange(opt.value)}
              aria-pressed={active}
              className={cn(
                "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
                active
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-input hover:border-primary/50",
              )}
            >
              <span
                className={cn(
                  "flex items-center justify-center w-10 h-10 rounded-md shrink-0",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                <Icon className="w-5 h-5" />
              </span>
              <span className="space-y-0.5">
                <span className="block text-sm font-medium">{opt.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {opt.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <SellerSelectField
        value={sellerId}
        onChange={onSellerChange}
        error={form.formState.errors.sellerId?.message}
      />
    </div>
  );
});
